import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBlog, BlogPost } from '../../context/BlogContext';
import { generateBlogDraft } from '../../services/blogService';
import { uploadImage } from '../../services/uploadService';
import { getImageUrl } from '../../utils/imageUtils';
import {
  ArrowLeft,
  Calendar,
  Edit,
  FileText,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  Sparkles,
  Trash,
  UploadCloud,
  Wand2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const emptyPost = (): Partial<BlogPost> => ({
  title: '',
  slug: '',
  date: new Date().toISOString().split('T')[0],
  author: 'Enzo Chevallier',
  excerpt: '',
  content: '',
  image: '',
  categories: [],
  tags: [],
  readTime: 5,
});

const slugFromTitle = (title: string) =>
  title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const responseError = error as {
      code?: string;
      response?: { data?: { message?: string } };
      message?: string;
    };
    if (responseError.code === 'ECONNABORTED') {
      return 'Draft and image generation took too long. Please retry with a narrower topic.';
    }

    return responseError.response?.data?.message || responseError.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const AdminBlog = () => {
  const { posts, addPost, updatePost, deletePost } = useBlog();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get('edit');
  const isNewPost = searchParams.get('new') === 'true';
  const isEditing = Boolean(editId || isNewPost);

  const [currentPost, setCurrentPost] = useState<Partial<BlogPost>>(emptyPost);
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiAdditionalDetails, setAiAdditionalDetails] = useState('');
  const [aiLanguage, setAiLanguage] = useState('English');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (editId) {
      const postToEdit = posts.find((post) => post.id === editId);
      if (postToEdit) {
        setCurrentPost(postToEdit);
      }
      return;
    }

    if (isNewPost) {
      setCurrentPost(emptyPost());
      return;
    }

    setCurrentPost(emptyPost());
  }, [editId, isNewPost, posts]);

  const sortedPosts = useMemo(
    () => [...posts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [posts]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setCurrentPost((previous) => ({
      ...previous,
      [name]: name === 'readTime' ? Number(value) : value,
    }));
  };

  const handleSlugGeneration = () => {
    if (!currentPost.title) return;
    setCurrentPost((previous) => ({
      ...previous,
      slug: slugFromTitle(currentPost.title || ''),
    }));
  };

  const addCategory = () => {
    const category = newCategory.trim();
    if (!category || currentPost.categories?.includes(category)) return;

    setCurrentPost((previous) => ({
      ...previous,
      categories: [...(previous.categories || []), category],
    }));
    setNewCategory('');
  };

  const removeCategory = (category: string) => {
    setCurrentPost((previous) => ({
      ...previous,
      categories: previous.categories?.filter((item) => item !== category) || [],
    }));
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag || currentPost.tags?.includes(tag)) return;

    setCurrentPost((previous) => ({
      ...previous,
      tags: [...(previous.tags || []), tag],
    }));
    setNewTag('');
  };

  const removeTag = (tag: string) => {
    setCurrentPost((previous) => ({
      ...previous,
      tags: previous.tags?.filter((item) => item !== tag) || [],
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setUploadError(null);
    setIsUploading(true);

    try {
      const result = await uploadImage(file);
      setCurrentPost((previous) => ({
        ...previous,
        image: result.filePath,
      }));
      toast.success('Image uploaded successfully');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Upload failed');
      setUploadError(message);
      toast.error(`Upload failed: ${message}`);
    } finally {
      setSelectedFile(null);
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleGenerateDraft = async () => {
    if (!aiTopic.trim()) {
      toast.error('Enter a topic first.');
      return;
    }

    setIsGenerating(true);
    try {
      const draft = await generateBlogDraft({
        topic: aiTopic.trim(),
        additionalDetails: aiAdditionalDetails.trim() || undefined,
        language: aiLanguage,
      });

      setCurrentPost((previous) => ({
        ...previous,
        title: draft.title,
        slug: draft.slug,
        excerpt: draft.excerpt,
        content: draft.content,
        image: draft.image || previous.image || '',
        categories: draft.categories,
        tags: draft.tags,
        readTime: draft.readTime,
        author: previous.author || 'Enzo Chevallier',
        date: previous.date || new Date().toISOString().split('T')[0],
      }));
      if (draft.imageWarning) {
        toast.error(`Draft generated, but image generation failed: ${draft.imageWarning}`);
      } else {
        toast.success('Draft and image generated. Review them before saving.');
      }
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Draft generation failed');
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!currentPost.title || !currentPost.slug || !currentPost.excerpt || !currentPost.content) {
      toast.error('Please fill in title, slug, excerpt, and content.');
      return;
    }

    if (!currentPost.image) {
      toast.error('Please upload an image for the post.');
      return;
    }

    setIsSaving(true);
    try {
      if (editId) {
        await updatePost(editId, currentPost as BlogPost);
        toast.success('Post updated successfully');
      } else {
        await addPost(currentPost as Omit<BlogPost, 'id'>);
        toast.success('Post created successfully');
      }
      navigate('/admin');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Save failed');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editId || !window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await deletePost(editId);
      toast.success('Post deleted successfully');
      navigate('/admin');
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Delete failed');
      toast.error(message);
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-container">
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-start gap-4">
            <Link to="/admin" className="admin-icon-button mt-1" aria-label="Back to admin">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <p className="admin-section-title mb-3">Blog</p>
              <h1 className="text-3xl font-bold sm:text-4xl">
                {editId ? 'Edit Blog Post' : isNewPost ? 'Create New Blog Post' : 'Blog Posts'}
              </h1>
            </div>
          </div>

          {isEditing && (
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={handleSave} disabled={isSaving} className="btn btn-primary">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Post
              </button>
              {editId && (
                <button type="button" onClick={handleDelete} className="btn btn-secondary text-error">
                  <Trash className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-6">
            <section className="admin-panel p-5 sm:p-6">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                <div>
                  <div className="mb-3 flex items-center gap-2 text-primary">
                    <Wand2 className="h-4 w-4" />
                    <p className="admin-section-title text-primary">AI Draft</p>
                  </div>
                  <label htmlFor="aiTopic" className="label">Topic</label>
                  <input
                    id="aiTopic"
                    type="text"
                    className="input"
                    placeholder="e.g. Gemini 3 for developers, AI agents in mobile apps..."
                    value={aiTopic}
                    onChange={(event) => setAiTopic(event.target.value)}
                    disabled={isGenerating}
                  />
                  <label htmlFor="aiAdditionalDetails" className="label mt-5">Additional Guidance</label>
                  <textarea
                    id="aiAdditionalDetails"
                    className="input min-h-[120px]"
                    placeholder="e.g. Codex is written in Rust; Claude Code ships new features quickly; Antigravity 2.0 feels close to Codex but Electron-based..."
                    value={aiAdditionalDetails}
                    onChange={(event) => setAiAdditionalDetails(event.target.value)}
                    disabled={isGenerating}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-[180px_auto]">
                  <div>
                    <label htmlFor="aiLanguage" className="label">Language</label>
                    <select
                      id="aiLanguage"
                      className="input"
                      value={aiLanguage}
                      onChange={(event) => setAiLanguage(event.target.value)}
                      disabled={isGenerating}
                    >
                      <option value="English">English</option>
                      <option value="French">French</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateDraft}
                    disabled={isGenerating}
                    className="btn btn-primary self-end"
                  >
                    {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Generate Draft
                  </button>
                </div>
              </div>
              <p className="mt-4 text-sm text-foreground/55">
                {isGenerating
                  ? 'Researching current sources, writing the draft, and generating the featured image. This can take up to 5 minutes.'
                  : 'The draft and image are generated but not saved. Review sources, facts, tone, and SEO fields before publishing.'}
              </p>
            </section>

            <section className="admin-panel p-5 sm:p-6">
              <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="title" className="label">Title*</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      className="input"
                      value={currentPost.title}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                    <div>
                      <label htmlFor="slug" className="label">Slug*</label>
                      <div className="grid grid-cols-[minmax(0,1fr)_auto]">
                        <input
                          type="text"
                          id="slug"
                          name="slug"
                          className="input rounded-r-none"
                          value={currentPost.slug}
                          onChange={handleInputChange}
                          required
                        />
                        <button
                          type="button"
                          onClick={handleSlugGeneration}
                          className="bg-primary/20 px-3 text-sm font-medium text-primary transition-colors hover:bg-primary/30"
                        >
                          Generate
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="date" className="label">Date*</label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        className="input"
                        value={currentPost.date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="excerpt" className="label">Excerpt*</label>
                    <textarea
                      id="excerpt"
                      name="excerpt"
                      rows={4}
                      className="input"
                      value={currentPost.excerpt}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="content" className="label">Content*</label>
                    <textarea
                      id="content"
                      name="content"
                      rows={24}
                      className="input min-h-[520px] font-mono text-sm leading-6"
                      value={currentPost.content}
                      onChange={handleInputChange}
                      required
                    />
                    <p className="mt-2 text-xs text-foreground/50">Content supports Markdown formatting.</p>
                  </div>
                </div>

                <aside className="space-y-6">
                  <div>
                    <label className="label">Featured Image*</label>
                    <div className="admin-panel-muted p-4">
                      {currentPost.image ? (
                        <img
                          src={getImageUrl(currentPost.image)}
                          alt="Current featured"
                          className="mb-4 h-44 w-full rounded-lg object-cover"
                        />
                      ) : (
                        <div className="mb-4 flex h-44 flex-col items-center justify-center rounded-lg border border-dashed border-foreground/15 text-center">
                          <ImageIcon className="h-10 w-10 text-foreground/35" />
                          <p className="mt-2 text-sm text-foreground/55">No image uploaded yet.</p>
                        </div>
                      )}

                      <label htmlFor="image-upload" className={`btn btn-secondary w-full cursor-pointer ${isUploading ? 'opacity-50' : ''}`}>
                        <UploadCloud className="h-4 w-4" />
                        {isUploading ? 'Uploading...' : selectedFile ? selectedFile.name : 'Upload an image'}
                        <input
                          id="image-upload"
                          name="image-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleImageUpload}
                          accept="image/*"
                          disabled={isUploading}
                        />
                      </label>
                      {uploadError && <p className="mt-2 text-sm text-error">{uploadError}</p>}
                      <p className="mt-3 text-xs text-foreground/50">Max 5MB. JPG, PNG, GIF, WEBP.</p>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="author" className="label">Author</label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      className="input"
                      value={currentPost.author}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label htmlFor="readTime" className="label">Read Time (minutes)</label>
                    <input
                      type="number"
                      id="readTime"
                      name="readTime"
                      className="input"
                      value={currentPost.readTime}
                      onChange={handleInputChange}
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="label">Categories</label>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {currentPost.categories?.map((category) => (
                        <span key={category} className="admin-chip">
                          {category}
                          <button type="button" onClick={() => removeCategory(category)} className="text-primary/70 hover:text-primary">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_44px]">
                      <input
                        type="text"
                        className="input rounded-r-none"
                        placeholder="Add category"
                        value={newCategory}
                        onChange={(event) => setNewCategory(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && addCategory()}
                      />
                      <button type="button" onClick={addCategory} className="bg-primary/20 text-primary hover:bg-primary/30">
                        <Plus className="mx-auto h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="label">Tags</label>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {currentPost.tags?.map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-foreground/10 bg-background/50 px-3 py-1 text-xs text-foreground/70">
                          {tag}
                          <button type="button" onClick={() => removeTag(tag)} className="text-foreground/50 hover:text-foreground">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_44px]">
                      <input
                        type="text"
                        className="input rounded-r-none"
                        placeholder="Add tag"
                        value={newTag}
                        onChange={(event) => setNewTag(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && addTag()}
                      />
                      <button type="button" onClick={addTag} className="bg-primary/20 text-primary hover:bg-primary/30">
                        <Plus className="mx-auto h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </aside>
              </div>
            </section>
          </div>
        ) : (
          <section className="admin-panel p-5 sm:p-6">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-bold">All Blog Posts</h2>
              <Link to="/admin/blog?new=true" className="btn btn-primary">
                <Plus className="h-4 w-4" />
                Create New Post
              </Link>
            </div>

            {sortedPosts.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {sortedPosts.map((post) => (
                  <article key={post.id} className="admin-panel-muted p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="mb-3 flex items-center gap-2 text-primary">
                          <FileText className="h-4 w-4" />
                          <span className="text-xs uppercase tracking-[0.18em]">Article</span>
                        </div>
                        <h3 className="line-clamp-2 font-semibold">{post.title}</h3>
                        <p className="mt-2 flex items-center gap-2 text-sm text-foreground/55">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.date).toLocaleDateString()}
                        </p>
                      </div>
                      <Link to={`/admin/blog?edit=${post.id}`} className="admin-icon-button" title="Edit post">
                        <Edit className="h-4 w-4" />
                      </Link>
                    </div>

                    <p className="mt-4 line-clamp-2 text-sm text-foreground/60">{post.excerpt}</p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {post.categories.map((category) => (
                        <span key={category} className="admin-chip">{category}</span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="admin-panel-muted p-8 text-center">
                <p className="mb-4 text-foreground/70">No blog posts found</p>
                <Link to="/admin/blog?new=true" className="btn btn-primary">
                  <Plus className="h-4 w-4" />
                  Create Your First Post
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminBlog;
